import { useContext, useState, useEffect, useCallback, ChangeEvent, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    Alert,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    FormLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    SelectChangeEvent
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { Text } from '@hubdevs/viu-ui-shared';
import { ApiServiceContext } from '../../api/ApiService.context';
import { Organization, OrganizationPermissions, OrganizationStatus } from '../../api/organization/types';
import { ApiServiceEndpointResponse, HttpError } from '../../api/api.types';
import { OrganizationAttributionSummary } from '../../api/attribution/types';
import { Bundle } from '../../api/bundle/types';
import { organizationAttributionColumns, bundleColumns, quoteExperienceColumns } from './gridColumns';
import { AppPage } from '../../components/Page/Page';
import { AppTableWithButtons, RowStatus } from '../../components/TableWithButtons/TableWithButtons';
import { AppFormRow } from '../../components/AppFormRow/AppFormRow';
import { SortConfig, DEFAULT_SORT } from '../../utils/sortUtils';
import { AppFormCell } from '../../components/AppFormCell/AppFormCell';
import './Organization.page.css'
import { AppTextField } from '../../components/AppTextField/AppTextField.component';
import { AppConfirmModal } from '../../components/AppConfirmModal/AppConfirmModal.component';
import { OrganizationLink } from '../../api/links/types';
import { LINK_TABLE_COLUMNS } from '../../api/links/constants';
import { VisibilityOff, Visibility, Lock, LockOpen, WarningAmber } from '@mui/icons-material';
import { toMachineName } from '../../utils/stringMethods';
import { APIQuery } from '../../utils/sharedTypes';
import { OrganizationService } from '../../api/organization/OrganizationService.class';
import { ApiServiceCollection } from '../../api/ApiServiceCollection.class';
import { BundleService } from '../../api/bundle/BundleService.class';
import { LinkService } from '../../api/links/LinkService.class';
import { TableRetryButton } from '../../components/TableWithButtons/TableRetryButton';

type OrgLoadingState = 'NOT STARTED' | 'LOADING' | 'DEFAULTS LOADED' | 'SUCCESS' | 'FAIL';
type LoadingState = 'NOT STARTED' | 'LOADING' | 'DEFAULTS LOADED' | 'SUCCESS' | 'FAIL';


export const OrganizationPage = () => {
    const navigate = useNavigate();
    const { organizationGuid } = useParams();
    const apiServiceContext = useContext(ApiServiceContext);
    const {
        OrganizationService,
        BundleService,
        LinkService
    } = apiServiceContext.getApiServiceCollections() as {
        OrganizationService: OrganizationService;
        BundleService: BundleService;
        LinkService: LinkService;
        [key: string]: ApiServiceCollection;
    };
    const [currentOrganization, setCurrentOrganization] = useState<Omit<Organization, 'organizationGuid'> & { organizationGuid?: string }>();
    const [organizationDefaults, setOrganizationDefaults] = useState<Omit<Organization, 'organizationGuid'>>();
    const [defaultsError, setDefaultsError] = useState<string>('');
    const [organizationError, setOrganizationError] = useState<string>('');
    const [machineNameError, setMachineNameError] = useState<string>('');
    const [orgLoadingState, setOrgLoadingState] = useState<OrgLoadingState>('NOT STARTED');

    const [displayName, setDisplayName] = useState<string>('');
    const [machineName, setMachineName] = useState<string>('');
    const [machineNameAutofill, setMachineNameAutofill] = useState<boolean>(true);
    const [machineNameDisabled, setMachineNameDisabled] = useState<boolean>(false);
    const [metadataType, setMetadataType] = useState<string>('');
    const [orgStatus, setOrgStatus] = useState<OrganizationStatus>('Active');

    const [bundles, setBundles] = useState<Bundle[]>();
    const [bundlesError, setBundlesError] = useState<string>('');
    const [totalBundleRecords, setTotalBundleRecords] = useState(0);
    const [paginationModelBundles, setPaginationModelBundles] = useState({ page: 0, pageSize: 10 });
    const [bundlesLoadingState, setBundlesLoadingState] = useState<LoadingState>('NOT STARTED');

    const [quoteExperiences, setQuoteExperiences] = useState<Bundle[]>();
    const [quoteExperiencesError, setQuoteExperiencesError] = useState<string>('');
    const [totalQuoteExperienceRecords, setTotalQuoteExperienceRecords] = useState(0);
    const [paginationModelQuoteExperiences, setPaginationModelQuoteExperiences] = useState({ page: 0, pageSize: 10 });
    const [quoteExperiencesLoadingState, setQuoteExperiencesLoadingState] = useState<LoadingState>('NOT STARTED');

    const [links, setLinks] = useState<OrganizationLink[]>();
    const [linkError, setLinkError] = useState<string>('');
    const [totalLinkRecords, setTotalLinkRecords] = useState(0);
    const [paginationModelLinks, setPaginationModelLinks] = useState({ page: 0, pageSize: 10 });
    const [linksLoadingState, setLinksLoadingState] = useState<LoadingState>('NOT STARTED');

    const [attributions, setAttributions] = useState<OrganizationAttributionSummary[]>();
    const [attributionsError, setAttributionsError] = useState<string>('');
    const [totalAttrRecords, setTotalAttrRecords] = useState(0);
    const [paginationModelAttr, setPaginationModelAttr] = useState({ page: 0, pageSize: 10 });
    const [attributionsLoadingState, setAttributionsLoadingState] = useState<LoadingState>('NOT STARTED');
    const [attrStatusToggle, setAttrStatusToggle] = useState<RowStatus>('ACTIVE');
    const [attrDisplayNameFilter, setAttrDisplayNameFilter] = useState<string>('');
    const [showSecret, setShowSecret] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [machineNameConfirmOpen, setMachineNameConfirmOpen] = useState(false);
    const [pendingSaveAndContinue, setPendingSaveAndContinue] = useState(false);
    const [attrSort, setAttrSort] = useState<SortConfig | null>(DEFAULT_SORT);

    const [permissions, setPermissions] = useState<OrganizationPermissions>({
        link: true,
        api: true,
        directToVat: true
    });

    const canSave = useMemo(() => {
        if (displayName) {
            return true;
        }
        return false;
    }, [displayName]);


    const machineNameAlreadySet = useMemo(() => {
        return Boolean(currentOrganization?.machineName);
    }, [ currentOrganization ]);

    const toggleMachineNameLock = () => {
        if (!machineNameDisabled && currentOrganization?.machineName) {
            setMachineName(currentOrganization.machineName);
        }
        setMachineNameDisabled(!machineNameDisabled);
    };

    const isDirty = useMemo(() => {
        if (currentOrganization?.displayName !== displayName) {
            return true;
        }

        if (currentOrganization?.metadataSchemaType !== metadataType) {
            return true;
        }

        if (currentOrganization?.status !== orgStatus) {
            return true;
        }

        if (currentOrganization?.machineName !== machineName) {
            return true;
        }

        return false;
    }, [currentOrganization, displayName, metadataType, orgStatus, machineName]);

    const getOrganizationDefaults = useCallback(() => {
        setDefaultsError('');
        const getOrganizationDefaultsEndpoint = OrganizationService.getOrganizationDefaults
        getOrganizationDefaultsEndpoint()
            .then(response => {
                const { organizationGuid, ...defaults } = response.jsonBody!;
                setOrganizationDefaults(defaults);
                setOrgLoadingState('DEFAULTS LOADED');
            })
            .catch(e => {
                setDefaultsError('Failed to load organization defaults');
                setOrgLoadingState('FAIL');
            });
    }, [OrganizationService]);

    const loadOrganization = useCallback(async () => {
        setOrganizationError('');
        if (!organizationGuid) {
            setOrgLoadingState('SUCCESS');
            const newOrg = {
                ...organizationDefaults!,
                displayName: '',
                machineName: '',
                metadataSchemaType: '',
            };
            setCurrentOrganization(newOrg);
            setDisplayName('');
            setMachineName('');
            setMetadataType('');
            setOrgStatus(newOrg.status);
            return;
        }
        let getOrgResp = await OrganizationService.getOrganizationById(organizationGuid);
        while (getOrgResp.retry) {
            getOrgResp = await getOrgResp.retry.catch(error => error);
        }
        const org = getOrgResp.jsonBody;
        if (getOrgResp.error || !org) {
            if (!org && !getOrgResp.error) {
                console.log('Uncaught logic error when failing to get organization info', getOrgResp);
            }
            setOrganizationError('Failed to load selected organization');
            setOrgLoadingState('FAIL');
            return;
        }
        setCurrentOrganization(org);
        setDisplayName(org.displayName);
        setMachineName(org.machineName || toMachineName(org.displayName));
        setMetadataType(org.metadataSchemaType || '');
        setOrgStatus(org.status);
        setOrgLoadingState('SUCCESS');
        if (org.machineName) {
            // Once a machine name is set on an Organization, it cannot be edited again.
            // Old Organizations might not have a machine name, so we need to allow updating
            // only in this scenario
            setMachineNameDisabled(true);
            setMachineNameAutofill(false);
        }
    }, [organizationGuid, organizationDefaults, OrganizationService]);

    const loadAttributions = useCallback(async (page: number, pageSize: number, query?: APIQuery) => {
        setAttributionsError('');
        if (!organizationGuid) {
            setAttributionsLoadingState('SUCCESS');
            setAttributions([] as OrganizationAttributionSummary[]);
            return;
        }
        let orgAttrsResp = await OrganizationService.getOrganizationAttributions({
            organizationGuid,
            skip: page * pageSize,
            limit: pageSize,
            query
        });
        while (orgAttrsResp.retry) {
            orgAttrsResp = await orgAttrsResp.retry.catch(error => error);
        }
        if (orgAttrsResp.error) {
            setAttributionsError('Failed to load organization attributions');
            setAttributionsLoadingState('FAIL');
            return
        }
        setAttributions(orgAttrsResp.jsonBody!.data);
        setTotalAttrRecords(orgAttrsResp.jsonBody!.total);
        setAttributionsLoadingState('SUCCESS');
    }, [organizationGuid, OrganizationService]);

    const loadBundles = useCallback(async (page: number, pageSize: number) => {
        setBundlesError('');
        if (!organizationGuid) {
            setBundlesLoadingState('SUCCESS');
            setBundles([] as Bundle[]);
            return;
        }
        let bundlesResp = await BundleService.getBundles({
            organizationGuid,
            skip: page * pageSize,
            limit: pageSize,
            query: {
                filter: {
                    type: 'STANDARD'
                }
            }
        });
        while (bundlesResp.retry) {
            // TODO - move retry to state and display attempts
            bundlesResp = await bundlesResp.retry.catch(error => error);
        }
        if (bundlesResp.error) {
            setBundlesError('Failed to load organization bundles')
            setBundlesLoadingState('FAIL');
            return
        }
        setBundles(bundlesResp.jsonBody!.data);
        setTotalBundleRecords(bundlesResp.jsonBody!.total);
        setBundlesLoadingState('SUCCESS');
    }, [organizationGuid, BundleService]);

    const loadQuoteExperiences = useCallback(async (page: number, pageSize: number) => {
        setQuoteExperiencesError('');
        if (!organizationGuid) {
            setQuoteExperiencesLoadingState('SUCCESS');
            setQuoteExperiences([] as Bundle[]);
            return;
        }
        let qeResp = await BundleService.getBundles({
            organizationGuid,
            skip: page * pageSize,
            limit: pageSize,
            query: {
                filter: {
                    type: 'QUOTE_EXPERIENCE'
                }
            }
        });
        while (qeResp.retry) {
            // TODO - move retry to state and display attempts
            qeResp = await qeResp.retry.catch(error => error);
        }
        if (qeResp.error) {
            setQuoteExperiencesError('Failed to load organization bundles')
            setQuoteExperiencesLoadingState('FAIL');
            return
        }
        setQuoteExperiences(qeResp.jsonBody!.data);
        setTotalQuoteExperienceRecords(qeResp.jsonBody!.total);
        setQuoteExperiencesLoadingState('SUCCESS');
    }, [organizationGuid, BundleService]);

    const loadLinks = useCallback(async (page: number, pageSize: number) => {
        setLinkError('');
        if (!organizationGuid) {
            setLinksLoadingState('SUCCESS');
            setLinks([] as OrganizationLink[]);
            return;
        }
        let linksResp = await LinkService.getLinks({ organizationGuid, skip: page * pageSize, limit: pageSize });
        while (linksResp.retry) {
            // TODO - move retry to state and display attempts
            linksResp = await linksResp.retry.catch(error => error);
        }
        if (linksResp.error) {
            setLinkError('Failed to load organization links')
            setLinksLoadingState('FAIL');
            return
        }
        setLinks(linksResp.jsonBody!.data);
        setTotalLinkRecords(linksResp.jsonBody!.total);
        setLinksLoadingState('SUCCESS');
    }, [organizationGuid, LinkService]);


    const saveOrganization = (andContinue: boolean = false) => {
        setDefaultsError('');
        setOrganizationError('');
        setMachineNameError('');
        if (!displayName) {
            setOrganizationError('Display Name is required');
            return;
        }
        if (!machineNameDisabled && !machineName) {
            setOrganizationError('Machine Name is required');
            return;
        }
        if (organizationGuid && machineNameAlreadySet && machineName !== currentOrganization?.machineName) {
            setPendingSaveAndContinue(andContinue);
            setMachineNameConfirmOpen(true);
            return;
        }
        submitOrganization(andContinue);
    }

    const submitOrganization = async (andContinue: boolean = false) => {
        setIsSaving(true);
        let orgPayload: Partial<Organization> = {
            displayName,
            status: orgStatus
        };
        let orgPromise: Promise<ApiServiceEndpointResponse<string>> | null = null;
        if (organizationGuid) {
            if (metadataType !== currentOrganization?.metadataSchemaType) {
                orgPayload.metadataSchemaType = metadataType;
            }

            orgPayload.machineName = machineName;
            orgPromise = OrganizationService.updateOrganization(orgPayload, organizationGuid);
        } else {
            orgPayload = {
                displayName,
                machineName,
                metadataSchemaType: metadataType,
                status: orgStatus
            };

            orgPromise = OrganizationService.createOrganization(orgPayload);
            // .then(response => {
            //     if (orgStatus !== 'Inactive' || !response?.jsonBody) {
            //         return response;
            //     }
            //     // Why immediately update after???
            //     // Commenting out for now. When things like this are done we need comments describing why.
            //     // return OrganizationService.updateOrganization(orgPayload, response.jsonBody)
            //     //     .then(() => response);
            // });
        }
        setOrgLoadingState('LOADING');
        const response = await orgPromise.catch(e => e);
        setIsSaving(false);
        if (response?.error) {
            const error = response.error as HttpError;
            setOrgLoadingState('SUCCESS');
            const isDuplicateMachineName = error?.status === 409 ||
                (error?.status === 400 && /machine.?name.+already exists/i.test(error?.message || ''));
            if (isDuplicateMachineName) {
                setMachineNameError('An Organization with this machine name already exists.');
                return;
            }
            setOrganizationError(`Failed to ${organizationGuid ? 'update' : 'save'} organization.`);
            return;
        }
        if (organizationGuid) {
            navigate(andContinue ? `/organization/${organizationGuid}` : '/');
        } else if (response?.textBody && andContinue) {
            const orgGuid = response.textBody && andContinue ? response.textBody : undefined;
            navigate(orgGuid ? `/organization/${orgGuid}` : '/');
        } else {
            navigate('/');
        }
    }

    const confirmMachineNameChange = () => {
        setMachineNameConfirmOpen(false);
        submitOrganization(pendingSaveAndContinue);
    }

    const cancelMachineNameChange = () => {
        setMachineNameConfirmOpen(false);
    }

    const associateAttributions = () => {
        navigate(`/organization/${organizationGuid}/attributions`)
    }

    const addQuoteExperience = () => {
        navigate(`/organization/${organizationGuid}/quote-experience`)
    }

    const addBundle = () => {
        navigate(`/organization/${organizationGuid}/bundles`)
    }

    const addLink = () => {
        navigate(`/organization/${organizationGuid}/links`)
    }

    const handleAttributionStatusChange = useCallback((rowStatus: RowStatus) => {
        setAttrStatusToggle(rowStatus);
        setPaginationModelAttr({ page: 0, pageSize: 10 });
        loadAttributions(0, 10, {
            status: rowStatus,
            filter: attrDisplayNameFilter ? {
                displayName: attrDisplayNameFilter
            } : undefined,
            sort: attrSort || undefined
        });
    }, [loadAttributions, attrDisplayNameFilter, attrSort]);

    const handleAttributionSearch = useCallback((searchTerm: string) => {
        setAttrDisplayNameFilter(searchTerm);
        setPaginationModelAttr({ page: 0, pageSize: 10 });
        loadAttributions(0, 10, {
            status: attrStatusToggle,
            filter: searchTerm ? {
                displayName: searchTerm
            } : undefined,
            sort: attrSort || undefined
        });
    }, [loadAttributions, attrStatusToggle, attrSort]);

    const handleAttributionSortChange = useCallback((sort: SortConfig | null) => {
        const effectiveSort = sort || DEFAULT_SORT;
        setAttrSort(effectiveSort);
        setPaginationModelAttr({ page: 0, pageSize: 10 });
        loadAttributions(0, 10, {
            status: attrStatusToggle,
            filter: attrDisplayNameFilter ? {
                displayName: attrDisplayNameFilter
            } : undefined,
            sort: effectiveSort || undefined
        });
    }, [loadAttributions, attrStatusToggle, attrDisplayNameFilter]);

    // Load Org
    useEffect(() => {
        if (orgLoadingState === 'NOT STARTED') {
            setOrgLoadingState('LOADING');
            getOrganizationDefaults();
            return;
        }
        if (orgLoadingState === 'DEFAULTS LOADED') {
            setOrgLoadingState('LOADING');
            loadOrganization();
            return;
        }
    }, [orgLoadingState, loadOrganization, getOrganizationDefaults]);

    // Load Attributions
    useEffect(() => {
        if (attributionsLoadingState === 'NOT STARTED') {
            setAttributionsLoadingState('LOADING');
            loadAttributions(paginationModelAttr.page, paginationModelAttr.pageSize);
        }
    }, [attributionsLoadingState, loadAttributions, paginationModelAttr.page, paginationModelAttr.pageSize, attrStatusToggle]);

    // Load Bundles
    useEffect(() => {
        if (bundlesLoadingState === 'NOT STARTED') {
            setBundlesLoadingState('LOADING');
            loadBundles(paginationModelBundles.page, paginationModelBundles.pageSize);
        }
    }, [bundlesLoadingState, loadBundles, paginationModelBundles.page, paginationModelBundles.pageSize]);

    // Load Quote Experiences
    useEffect(() => {
        if (quoteExperiencesLoadingState === 'NOT STARTED') {
            setQuoteExperiencesLoadingState('LOADING');
            loadQuoteExperiences(paginationModelQuoteExperiences.page, paginationModelQuoteExperiences.pageSize);
        }
    }, [quoteExperiencesLoadingState, loadQuoteExperiences, paginationModelQuoteExperiences.page, paginationModelQuoteExperiences.pageSize]);

    // Load Links
    useEffect(() => {
        if (linksLoadingState === 'NOT STARTED') {
            setLinksLoadingState('LOADING');
            loadLinks(paginationModelLinks.page, paginationModelLinks.pageSize);
        }
    }, [linksLoadingState, loadLinks, paginationModelLinks.page, paginationModelLinks.pageSize]);

    // Load Permissions
    useEffect(() => {
        if (!currentOrganization) {
            return;
        }
        // Temporary until permissions are returned
        setPermissions({
            link: true,
            api: true,
            directToVat: true
        });
    }, [currentOrganization]);

    const handlePageChangeLinks = useCallback((page: number, pageSize: number) => {
        setPaginationModelLinks({ page, pageSize });
        loadLinks(page, pageSize);
    }, [loadLinks]);

    const handlePageChangeAttr = useCallback((page: number, pageSize: number) => {
        setPaginationModelAttr({ page, pageSize });
        loadAttributions(page, pageSize, {
            status: attrStatusToggle,
            filter: attrDisplayNameFilter ? {
                displayName: attrDisplayNameFilter
            } : undefined,
            sort: attrSort || undefined
        });
    }, [loadAttributions, attrStatusToggle, attrDisplayNameFilter, attrSort]);

    const handlePageChangeBundles = useCallback((page: number, pageSize: number) => {
        setPaginationModelBundles({ page, pageSize });
        loadBundles(page, pageSize);
    }, [loadBundles]);

    const handlePageChangeQuoteExperiences = useCallback((page: number, pageSize: number) => {
        setPaginationModelQuoteExperiences({ page, pageSize });
        loadQuoteExperiences(page, pageSize);
    }, [loadQuoteExperiences]);

    const isPageLoading =
        orgLoadingState === 'NOT STARTED' || orgLoadingState === 'LOADING' ||
        attributionsLoadingState === 'LOADING' ||
        bundlesLoadingState === 'LOADING' ||
        quoteExperiencesLoadingState === 'LOADING' ||
        linksLoadingState === 'LOADING' ||
        isSaving

    const rowStatusAndPermissions = () => (
        <AppFormRow>
            <AppFormCell>
                <FormLabel htmlFor="status" >Status</FormLabel>
                <FormGroup row>
                    <Select
                        name="status"
                        value={orgStatus}
                        onChange={(event: SelectChangeEvent<string>) => setOrgStatus(event.target.value as OrganizationStatus)}
                    >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                </FormGroup>
            </AppFormCell>
            <AppFormCell>
                <FormLabel>Permissions</FormLabel>
                <FormGroup row sx={{ gap: "1.67em" }}>
                    <FormControlLabel
                        label="Link"
                        control={
                            <Checkbox
                                name="link"
                                disabled={true}
                                checked={permissions.link}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPermissions({
                                    ...permissions,
                                    link: event.target.checked
                                })}
                            />
                        }
                    />
                    <FormControlLabel
                        label="API"
                        control={
                            <Checkbox
                                name="api"
                                disabled={true}
                                checked={permissions.api}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPermissions({
                                    ...permissions,
                                    api: event.target.checked
                                })}
                            />
                        }
                    />
                    <FormControlLabel
                        label="DirectToVAT"
                        control={
                            <Checkbox
                                name="directToVat"
                                disabled={true}
                                checked={permissions.directToVat}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPermissions({
                                    ...permissions,
                                    directToVat: event.target.checked
                                })}
                            />
                        }
                    />
                </FormGroup>
            </AppFormCell>
        </AppFormRow>
    )

    const getMachineNameWarningRow = () => {
        if (!machineNameAlreadySet || machineNameDisabled) {
            return null;
        }
        return (
            <AppFormRow>
                <AppFormCell>
                    <Alert severity="warning">
                        WARNING: Editing a machine name for an Organization may break hardcoded linkage between services. Only edit this field if you are sure it is necessary.
                    </Alert>
                </AppFormCell>
            </AppFormRow>
        );
    };

    const getOrganizationIdentifierFields = () => (
        <AppFormRow>
            <AppFormCell>
                <FormLabel htmlFor="name" >Name</FormLabel>
                <FormGroup row>
                    <AppTextField
                        required
                        name="name"
                        inputProps={{ "data-testid": "name" }}
                        value={displayName}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setDisplayName(event.target.value);
                            if (machineNameAutofill) {
                                setMachineName(toMachineName(event.target.value));
                            }
                        }}
                    />
                </FormGroup>
            </AppFormCell>
            <AppFormCell>
                <FormLabel htmlFor="machine_name" >Machine Name</FormLabel>
                <FormGroup row>
                    <AppTextField
                        required
                        disabled={machineNameDisabled}
                        name="machine_name"
                        inputProps={{ "data-testid": "machine_name" }}
                        value={machineName}
                        error={!!machineNameError}
                        helperText={machineNameError}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setMachineName(event.target.value);
                            setMachineNameAutofill(false);
                            setMachineNameError('');
                        }}
                        InputProps={ machineNameAlreadySet ? {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={ machineNameDisabled ? 'unlock machine name' : 'lock machine name' }
                                        onClick={toggleMachineNameLock}
                                        edge="end"
                                    >
                                        { machineNameDisabled ? <Lock /> : <LockOpen /> }
                                    </IconButton>
                                </InputAdornment>
                            )
                        } : undefined }
                    />
                </FormGroup>
            </AppFormCell>
            <AppFormCell>
                <FormLabel htmlFor="metadataSchema" >Metadata Schema Type</FormLabel>
                <FormGroup row>
                    <AppTextField
                        name="metadataType"
                        inputProps={{ "data-testid": "metadataType" }}
                        value={metadataType}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setMetadataType(event.target.value)
                        }}
                    />
                </FormGroup>
            </AppFormCell>

        </AppFormRow>
    );

    const getLinksRow = () => (
        <AppFormRow>
            <AppFormCell style={{ display: 'flex', flexDirection: 'column', maxWidth: 1100 }}>
                <AppTableWithButtons
                    rowIdField='linkGuid'
                    rows={links || []}
                    pagination
                    totalRecords={totalLinkRecords}
                    isLoading={linksLoadingState === 'LOADING'}
                    onPageChange={handlePageChangeLinks}
                    columns={[
                        ...LINK_TABLE_COLUMNS,
                        {
                            field: 'edit',
                            headerName: 'Edit',
                            width: 100,
                            editable: false,
                            renderCell: (cellValues) => {
                                return <Link to={`/organization/${cellValues.row.organizationGuid}/links/${cellValues.row.linkGuid}`}>Edit</Link>
                            }
                        }
                    ]}
                    title="Links"
                    slots={{
                        noRowsOverlay: TableRetryButton({
                            message: linkError,
                            retry: () => setLinksLoadingState('NOT STARTED')
                        })
                    }}
                >
                    <Button
                        disabled={!organizationGuid}
                        sx={{ alignSelf: "end" }}
                        variant='outlined'
                        onClick={() => addLink()}
                    >Add Link</Button>
                </AppTableWithButtons>
            </AppFormCell>
        </AppFormRow>
    );

    const getClientIdSecretRow = () => {

        if (!organizationGuid || !currentOrganization?.clientId || !currentOrganization?.clientSecret) return null;
        const handleCopy = () => {
            navigator.clipboard.writeText(currentOrganization.clientSecret!);
        };

        return (
            <AppFormRow>
                <AppFormCell>
                    <FormLabel>Client ID</FormLabel>
                    <FormGroup row>
                        <AppTextField
                            disabled
                            name="clientId"
                            inputProps={{ "data-testid": "clientId" }}
                            value={currentOrganization?.clientId}
                        />
                    </FormGroup>
                </AppFormCell>
                <AppFormCell>
                    <FormLabel>Client Secret</FormLabel>
                    <FormGroup row>
                        <AppTextField
                            name="clientSecret"
                            type={showSecret ? "text" : "password"}
                            fullWidth
                            disabled
                            inputProps={{ "data-testid": "clientSecret" }}
                            value={currentOrganization?.clientSecret}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowSecret(!showSecret)} edge="end">
                                            {showSecret ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                        <IconButton onClick={handleCopy} edge="end">
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </FormGroup>
                </AppFormCell>
            </AppFormRow>
        );
    };

    const getAttributionsRow = () => (
        <AppFormRow>
            <AppFormCell>
                <AppTableWithButtons
                    rowIdField='attributionGuid'
                    pagination
                    totalRecords={totalAttrRecords}
                    rows={attributions || []}
                    isLoading={attributionsLoadingState === 'LOADING'}
                    columns={organizationAttributionColumns}
                    title="Attributions Associated with this Organization"
                    onPageChange={handlePageChangeAttr}
                    showStatusToggle={true}
                    rowStatus={attrStatusToggle}
                    onStatusChange={(rowStatus: RowStatus) => {
                        handleAttributionStatusChange(rowStatus);
                    }}
                    defaultSort={DEFAULT_SORT}
                    onSortChange={handleAttributionSortChange}
                    showSearch
                    onSearch={handleAttributionSearch}
                    slots={{
                        noRowsOverlay: TableRetryButton({
                            message: attributionsError,
                            retry: () => setAttributionsLoadingState('NOT STARTED')
                        })
                    }}
                >
                    <Button
                        disabled={!organizationGuid}
                        variant='outlined'
                        onClick={() => associateAttributions()}
                    >Associate Attributions</Button>
                </AppTableWithButtons>
            </AppFormCell>
        </AppFormRow>
    );


    const getBundlesRow = () => (
        <AppFormRow>
            <AppFormCell>
                <AppTableWithButtons
                    rowIdField='bundleGuid'
                    pagination
                    totalRecords={totalBundleRecords}
                    rows={bundles}
                    isLoading={bundlesLoadingState === 'LOADING'}
                    columns={bundleColumns}
                    title="Attribution Bundles"
                    onPageChange={handlePageChangeBundles}
                    slots={{
                        noRowsOverlay: TableRetryButton({
                            message: bundlesError,
                            retry: () => setBundlesLoadingState('NOT STARTED')
                        })
                    }}
                >
                    <Button
                        disabled={!organizationGuid}
                        variant='outlined'
                        onClick={() => addBundle()}
                    >Add Attribution Bundle</Button>
                </AppTableWithButtons>
            </AppFormCell>
        </AppFormRow>
    );

    const getQuoteExperiencesRow = () => (
        <AppFormRow>
            <AppFormCell>
                <AppTableWithButtons
                    rowIdField='bundleGuid'
                    pagination
                    totalRecords={totalQuoteExperienceRecords}
                    rows={quoteExperiences}
                    isLoading={quoteExperiencesLoadingState === 'LOADING'}
                    columns={quoteExperienceColumns}
                    title="Quote Experiences"
                    onPageChange={handlePageChangeQuoteExperiences}
                    slots={{
                        noRowsOverlay: TableRetryButton({
                            message: quoteExperiencesError,
                            retry: () => setQuoteExperiencesLoadingState('NOT STARTED')
                        })
                    }}
                >
                    <Button
                        disabled={!organizationGuid}
                        variant='outlined'
                        onClick={() => addQuoteExperience()}
                    >Add Quote Experience</Button>
                </AppTableWithButtons>
            </AppFormCell>
        </AppFormRow>
    )

    function organizationForm() {
        if (!currentOrganization) {
            return null;
        }
        return (
            <>
                { getOrganizationIdentifierFields() }
                { getMachineNameWarningRow() }
                { rowStatusAndPermissions() }
                { getClientIdSecretRow()}
                { getAttributionsRow() }
                { getLinksRow() }
                { getQuoteExperiencesRow()}
                { getBundlesRow() }
            </>
        )

        // Temporarily removed
        // { rowOkta() }
    }

    function getFormDisplay() {
        switch (orgLoadingState) {
            case 'FAIL':
                return (
                    <AppFormRow flex>
                        <AppFormCell>
                            {defaultsError ? <Text>{defaultsError}</Text> : <Text>{organizationError}</Text>}
                        </AppFormCell>
                    </AppFormRow>
                );
            case 'SUCCESS':
                return organizationForm();
            case 'NOT STARTED':
            case 'LOADING':
            default:
                return null;
        }
    }


    return (
        <AppPage
            title='Organization'
            isLoading={isPageLoading}
            buttonRowContent={(
                <>
                    <AppFormCell>
                        <Button variant="outlined" onClick={() => navigate('/')}>Cancel</Button>
                    </AppFormCell>
                    {orgLoadingState === 'SUCCESS' && (
                        <>
                            <AppFormCell>
                                <Button
                                    disabled={!canSave || !isDirty || isSaving}
                                    variant="outlined"
                                    onClick={() => saveOrganization()}
                                >
                                    Save
                                </Button>
                            </AppFormCell>
                            <AppFormCell>
                                <Button
                                    disabled={!canSave || !isDirty || isSaving}
                                    variant="outlined"
                                    onClick={() => saveOrganization(true)}
                                >
                                    Save and Continue
                                </Button>
                            </AppFormCell>
                        </>
                    )}
                </>
            )}
        >
            {getFormDisplay() }
            { organizationError }
            <AppConfirmModal
                open={machineNameConfirmOpen}
                title="WARNING"
                icon={<WarningAmber color="warning" />}
                message="You are about to edit the machine name of this Organization. Doing so may break linkage between services. Are you sure you wish to proceed?"
                onConfirm={confirmMachineNameChange}
                onCancel={cancelMachineNameChange}
            />
        </AppPage>
    )
}